/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { generatePath } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import {
  AlertSingleLine,
  DataTable,
  SdiArrow,
  SdiCheck,
  SdiLicense,
  SdirButton,
  SdiReport,
  SdirModal,
  styles,
  TextLink,
} from '@sdir/sds';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { CertificateStatus } from '@sdir/httpclient/lib/clients/aps/personalcertificate';
import {
  EndorsementServiceFeaturesGetOwnEndorsementsDispensationResponse as DispensationDto,
  EndorsementServiceFeaturesGetOwnEndorsementsGetOwnEndorsementsDtoEndorsement as EndorsementDto,
} from '@sdir/httpclient/lib/clients/aps/endorsement';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import {
  Dispensation,
  DispensationExtended,
  Endorsement,
  ErrorWithTrace,
} from '@sdir/blueprint.aps/lib/components';
import { formatDateString } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
// eslint-disable-next-line max-len
import { DispensationServiceFeaturesDispensationsSharedDispensationDto } from '@sdir/httpclient/lib/clients/aps/dispensation';
import { PageHeader } from '../../Atoms';
import { routes } from '../../../constants/routes';
import { dispensationApi, getApplicationApi } from '../../../httpclient';
import {
  CapitalizeString,
  filterOutExpiredCertificates,
  GetCertificateNameByCode,
} from '../../../helpers/Certificates';
import { useOwnCertificates } from '../../../services/hooks/useOwnCertificates';
import {
  ToggleApplications,
  ToggleCertificates,
  ToggleEndorsement,
} from '../../../constants/permissions';
import {
  useCertificateCodes,
  useCurrentPerson,
  useFeatureToggles,
  useOwnEndorsements,
} from '../../../services/hooks';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import EmptyStateImage from '../../../Assets/no_applications.svg';
import { ApplicationStatus } from '../../../types';
import {
  AllCertificatesHeaders,
  AllDispensationsHeaders,
  AllEndorsementsHeaders,
} from '../../../helpers/TableHeaders';
// eslint-disable-next-line max-len
import CertificateModalContent from '../../Organisms/CertificateModalContent/CertificateModalContent';
import { messages } from '../../../localization';

const CertificateOverviewPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const [showCertificateModal, setShowCertificateModal] = useState<boolean>(false);
  const [selectedCertificate, setSelectedCertificate] = useState<any>();
  const [hideExpired, setHideExpired] = useState<boolean>(false);
  const [selectedEndorsement, setSelectedEndorsement] = useState<EndorsementDto>();
  const [selectedDispensation, setSelectedDispensation] = useState<DispensationDto>();
  const [selectedFullDispensation, setSelectedFullDispensation] =
    useState<DispensationServiceFeaturesDispensationsSharedDispensationDto>();
  const [showEndorsement, setShowEndorsement] = useState<boolean>(false);
  const [showDispensation, setShowDispensation] = useState<boolean>(false);
  const { ownEndorsements } = useOwnEndorsements();
  const featureToggle = useFeatureToggles();
  const codes = useCertificateCodes();

  const {
    person: { result: contactInfo },
  } = useCurrentPerson();

  const userPersonalNr = useMemo(() => {
    return contactInfo?.personalIdentificationNumber || '';
  }, [contactInfo]);

  const {
    result: dispensationsResult,
    loading: dispensationsLoading,
    errorObject: dispensationsError,
  } = useApi({
    apiFunction: () => dispensationApi.getsOwnDispensations(),
    callApiOnInit: true,
  });

  const showCertificate = !isToggleOn(featureToggle?.result, ToggleCertificates, userPersonalNr);
  const showApplications = !isToggleOn(featureToggle?.result, ToggleApplications, userPersonalNr);
  const showEndorsements = !isToggleOn(featureToggle?.result, ToggleEndorsement, userPersonalNr);
  const hasEndorsements = ownEndorsements?.result && ownEndorsements?.result?.length > 0;
  const hasDispensations = dispensationsResult?.result && dispensationsResult?.result?.length > 0;

  const {
    ownCertificates: { result: certResult, loading: certLoading, error: certError },
  } = useOwnCertificates();

  const {
    result: appResult,
    loading: appLoading,
    errorObject: appError,
  } = useApi({
    apiFunction: () =>
      getApplicationApi.v2GetGetownapplicationsQueryPost({
        filter: {
          status: undefined,
          freeTextSearchStringOnType: undefined,
          applicationTypes: undefined,
        },
        skip: 0,
        take: 1000,
      }),
    callApiOnInit: true,
  });

  const allEndorsements = useMemo(() => {
    return (ownEndorsements?.result || []).map((e) => {
      const date = e?.endorsement?.issuedAt || '';
      return {
        name: CapitalizeString(e?.endorsement?.title || ''),
        issuedAt: date ? moment(date).format('DD.MM.YYYY') : '',
        actions: (
          <>
            {e?.endorsement && (
              <TextLink
                text={intl.formatMessage({ id: 'endorsements.page.endorsement' })}
                icon={<SdiLicense color={styles.colors.primary} size="s" />}
                onClick={() => onOpenEndorsement(e.endorsement)}
              />
            )}
            {e?.dispensation && (
              <TextLink
                text={intl.formatMessage({ id: 'endorsements.page.dispensation' })}
                icon={<SdiReport color={styles.colors.primary} size="s" />}
                onClick={() => onOpenDispensation(e.dispensation)}
              />
            )}
          </>
        ),
      };
    });
  }, [ownEndorsements?.result]);

  const allDispensations = useMemo(() => {
    return (dispensationsResult?.result || []).map((d) => {
      const expiryDate = d?.periodEnd ? moment(d?.periodEnd) : '';
      const isExpired = expiryDate ? expiryDate.isBefore(moment()) : false;
      const certName = CapitalizeString(d?.certificateName || '');
      return {
        name: isExpired ? <div className="expired">{certName}</div> : certName,
        validTo:
          isExpired && expiryDate
            ? `${intl.formatMessage({ id: 'general.table.expired' })} ${expiryDate.format(
                'DD.MM.YYYY'
              )}`
            : expiryDate
            ? expiryDate.format('DD.MM.YYYY')
            : '',
        actions: (
          <TextLink
            text={intl.formatMessage({ id: 'endorsements.page.dispensation' })}
            icon={<SdiReport color={styles.colors.primary} size="s" />}
            onClick={() => onOpenFullDispensation(d)}
          />
        ),
      };
    });
  }, [dispensationsResult?.result]);

  const { allCerts, activeCerts } = useMemo(() => {
    if (!certResult) return { allCerts: [], activeCerts: [] };
    const today = new Date();
    const REMINDER_DAYS = 60;
    const all = certResult.map((c) => {
      const certName = CapitalizeString(c.certificateName || '');
      const isExpired = c.status === CertificateStatus.Expired;
      const expiryAsDate = new Date(c.expiryDate || '');
      const daysLeft = Math.ceil((expiryAsDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
      return {
        certificate: isExpired ? <div className="expired">{certName}</div> : certName,
        expiry: isExpired ? (
          `${intl.formatMessage({ id: 'general.table.expired' })} ${formatDateString(
            c.expiryDate || ''
          )}`
        ) : daysLeft < REMINDER_DAYS ? (
          <>
            <ExpiryText>{formatDateString(c.expiryDate || '')}</ExpiryText>
            {daysLeft < 0 ? (
              <DaysLeft>{intl.formatMessage({ id: 'certificates.label.expired' })}</DaysLeft>
            ) : (
              <DaysLeft>
                {daysLeft} {intl.formatMessage({ id: 'certificates.label.daysleft' })}
              </DaysLeft>
            )}
          </>
        ) : (
          <ExpiryText>{formatDateString(c.expiryDate || '')}</ExpiryText>
        ),
        actions: c?.isDigitallyViewable ? (
          <TextLink
            text={intl.formatMessage({ id: 'certificates.button.showcertificate' })}
            onClick={() => onOpenCertificateModal(c)}
          />
        ) : (
          <></>
        ),
      };
    });
    const filtered = filterOutExpiredCertificates(certResult);
    const active = filtered.map((c) => {
      const certName = CapitalizeString(c.certificateName || '');
      const expiryAsDate = new Date(c.expiryDate || '');
      const daysLeft = Math.ceil((expiryAsDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
      return {
        certificate: certName,
        expiry:
          daysLeft < REMINDER_DAYS ? (
            <>
              <ExpiryText>{formatDateString(c.expiryDate || '')}</ExpiryText>
              {daysLeft < 0 ? (
                <DaysLeft>{intl.formatMessage({ id: 'certificates.label.expired' })}</DaysLeft>
              ) : (
                <DaysLeft>
                  {daysLeft} {intl.formatMessage({ id: 'certificates.label.daysleft' })}
                </DaysLeft>
              )}
            </>
          ) : (
            <ExpiryText>{formatDateString(c.expiryDate || '')}</ExpiryText>
          ),
        actions: c?.isDigitallyViewable ? (
          <TextLink
            text={intl.formatMessage({ id: 'certificates.button.showcertificate' })}
            onClick={() => onOpenCertificateModal(c)}
          />
        ) : (
          <></>
        ),
      };
    });
    return {
      allCerts: all || [],
      activeCerts: active || [],
    };
  }, [certResult]);

  const additionalInfoApplications = useMemo(() => {
    const applications = appResult?.result || [];
    const additionalInfoRequested = applications
      .filter((a) => a.applicationStatus === ApplicationStatus.RequestMoreInformation)
      .map((a) => ({
        certificate: GetCertificateNameByCode(intl?.locale, a.certificate, codes.result) || '',
        link: generatePath(routes.application.details, { applicationUid: a.uid || '' }),
      }));
    return additionalInfoRequested;
  }, [appResult?.result, codes?.result, intl?.locale]);

  const showEmptyState = useMemo(() => {
    if (certLoading || certError) return false;
    if (hasEndorsements || ownEndorsements?.loading) return false;
    if (hasDispensations || dispensationsLoading) return false;
    if (certResult) {
      return certResult.length === 0;
    }
    return false;
  }, [
    certResult,
    certLoading,
    certError,
    hasDispensations,
    hasEndorsements,
    dispensationsLoading,
    ownEndorsements?.loading,
  ]);

  const backToDashboard = useCallback(() => {
    navigate(routes.home);
  }, [navigate]);

  const toggleExpired = () => {
    setHideExpired(!hideExpired);
  };

  const onOpenCertificateModal = (certificate) => {
    setSelectedCertificate(certificate);
    setShowCertificateModal(true);
  };

  const onCloseCertificateModal = () => {
    setShowCertificateModal(false);
    setSelectedCertificate(undefined);
  };

  const onOpenEndorsement = (endorsement: EndorsementDto) => {
    setSelectedEndorsement(endorsement);
    setShowEndorsement(true);
  };
  const onCloseEndorsement = () => {
    setSelectedEndorsement(undefined);
    setShowEndorsement(false);
  };

  const onOpenDispensation = (dispensation?: DispensationDto | null) => {
    if (!dispensation) return;
    setSelectedDispensation(dispensation);
    setShowDispensation(true);
  };

  const onOpenFullDispensation = (
    dispensation?: DispensationServiceFeaturesDispensationsSharedDispensationDto | null
  ) => {
    if (!dispensation) return;
    setSelectedFullDispensation(dispensation);
    setShowDispensation(true);
  };
  const onCloseDispensation = () => {
    setSelectedDispensation(undefined);
    setShowDispensation(false);
  };

  const onOpenApplications = () => {
    navigate(routes.applicationsOverview);
  };

  if (!showCertificate && !showEndorsements) {
    return (
      <AlertSingleLine
        title="Error"
        type="error"
        text={intl.formatMessage({ id: 'utils.errors.insufficientaccess' })}
      />
    );
  }

  return (
    <Wrapper>
      <PageHeader
        backText={intl.formatMessage({ id: 'back.to.dashboard' })}
        heading={intl.formatMessage({ id: 'certificates.page.title' })}
        primaryIcon={SdiLicense}
        secondaryIcon={SdiCheck}
        backClick={backToDashboard}
      />
      {showEmptyState && (
        <EmptyState>
          <img src={EmptyStateImage} alt="No certificates found" width={300} height={282} />
          <span className="empty-title">
            {intl.formatMessage({ id: 'certificates.empty.title' })}
          </span>
        </EmptyState>
      )}
      <ButtonRow className={showEmptyState ? 'centered' : ''}>
        {showApplications && (
          <CustomButton type="secondary" onClick={onOpenApplications}>
            <div>
              <span>{intl.formatMessage({ id: 'certificates.button.myapplications' })}</span>
              <span>{intl.formatMessage({ id: 'certificates.button.myapplications.info' })}</span>
              <SdiArrow
                color={styles.colors.secondary.green1}
                size="s"
                height="8px"
                direction="right"
              />
            </div>
          </CustomButton>
        )}
      </ButtonRow>
      {additionalInfoApplications?.length > 0 && (
        <RequestMoreInfoWrapper>
          <AlertSingleLine
            hideClose
            title=""
            text={intl.formatMessage({ id: 'certificates.requestmoreinfo.title' })}
            type="warn"
          >
            <div className="request-more-info-content">
              <FormattedMessage
                id="certificates.requestmoreinfo.description"
                values={{
                  b: (chunks) => <b>{chunks}</b>,
                  certificate: additionalInfoApplications[0]?.certificate,
                }}
              />
            </div>
            <SdirButton
              type="ghost-dark"
              text={intl.formatMessage({ id: 'general.button.open.application' })}
              onClick={() => navigate(additionalInfoApplications[0]?.link)}
            />
          </AlertSingleLine>
        </RequestMoreInfoWrapper>
      )}
      {appError && (
        <ErrorWithTrace error={appError} trace={appError?.response?.headers?.traceresponse} />
      )}
      {!showEmptyState && allCerts && allCerts.length > 0 && (
        <CertificateTableWrapper>
          <StyledDataTable
            data={hideExpired ? activeCerts : allCerts}
            columns={AllCertificatesHeaders(intl.formatMessage)}
          />
          <TextLink
            text={
              hideExpired
                ? intl.formatMessage({ id: 'certificates.table.showexpired' })
                : intl.formatMessage({ id: 'certificates.table.hideexpired' })
            }
            onClick={toggleExpired}
          />
        </CertificateTableWrapper>
      )}
      {!showEmptyState && hasEndorsements && (
        <EndorsementTableWrapper>
          <StyledDataTable
            data={allEndorsements || []}
            columns={AllEndorsementsHeaders(intl.formatMessage)}
          />
        </EndorsementTableWrapper>
      )}
      {!showEmptyState && hasDispensations && (
        <DispensationsTableWrapper>
          <StyledDataTable
            data={allDispensations || []}
            columns={AllDispensationsHeaders(intl.formatMessage)}
          />
        </DispensationsTableWrapper>
      )}
      <StyledModal visible={showCertificateModal} onCancel={onCloseCertificateModal}>
        <h2>{intl.formatMessage({ id: 'certificate.page.title' })}</h2>
        <CertificateModalContent certificate={selectedCertificate} />
      </StyledModal>
      <StyledModal visible={showDispensation} onCancel={onCloseDispensation}>
        <div translate="no">
          {selectedFullDispensation ? (
            <DispensationExtended
              dateOfBirth={selectedFullDispensation?.seafarer?.dateOfBirth || ''}
              name={selectedFullDispensation?.seafarer?.name || ''}
              issuedDate={selectedFullDispensation?.issuedAt || ''}
              certificateName={selectedFullDispensation?.certificateName || ''}
              authorizedOfficial={selectedFullDispensation?.authorizedOfficial || ''}
              callsign={selectedFullDispensation?.vessel?.callSign || ''}
              dispensationNr={selectedFullDispensation?.dispensationNumber || ''}
              legalAuthority={selectedFullDispensation?.legalAuthority || ''}
              nationality={selectedFullDispensation?.seafarer?.nationality || ''}
              occupationName={selectedFullDispensation?.occupationTitle || ''}
              remarks={selectedFullDispensation?.remark || ''}
              validFrom={selectedFullDispensation?.periodStart}
              validTo={selectedFullDispensation?.periodEnd}
              vesselName={selectedFullDispensation?.vessel?.name || ''}
              qrImageLoading={false}
              // eslint-disable-next-line max-len
              qrData={`${process.env.REACT_APP_VERIFY_URL}/dispensation/${selectedFullDispensation?.dispensationNumber}`}
              hideLanguageSelector
            />
          ) : (
            <Dispensation
              applicationDate={selectedDispensation?.issuedAt || ''}
              citizenship={selectedDispensation?.seafarerNationality || ''}
              dateOfBirth={selectedDispensation?.seafarerDateOfBirth || ''}
              name={selectedDispensation?.seafarerName || ''}
              validDate={selectedDispensation?.expiresAt || ''}
            />
          )}
        </div>
      </StyledModal>
      <StyledModal visible={showEndorsement} onCancel={onCloseEndorsement}>
        <div translate="no">
          <Endorsement
            authority={selectedEndorsement?.nationalAuthority || ''}
            certificateNr={selectedEndorsement?.certificateNumber || ''}
            endorsementNr={selectedEndorsement?.endorsementNumber || ''}
            dateOfBirth={selectedEndorsement?.seafarerDateOfBirth || ''}
            name={selectedEndorsement?.seafarerName || ''}
            defaultLang="en"
            positionList={[
              {
                position: selectedEndorsement?.title || '',
                limitations: [messages.en['endorsements.page.limitation']],
              },
            ]}
            validDate={selectedEndorsement?.expiresAt || ''}
            isStcwF={selectedEndorsement?.regulationReference === 'STCW_F'}
            issuedDate={selectedEndorsement?.issuedAt}
            qrImageLoading={false}
            // eslint-disable-next-line max-len
            qrData={`${process.env.REACT_APP_VERIFY_URL}/en/endorsement/${selectedEndorsement?.endorsementNumber}`}
            hideLanguageSelector
          />
        </div>
      </StyledModal>
    </Wrapper>
  );
};
export default CertificateOverviewPage;

const Wrapper = styled.div`
  padding-top: 20px;
  padding-bottom: 60px;
  display: grid;
  .sectionheader {
    margin-top: 5em;
  }
  h1 {
    margin-bottom: 0;
  }
`;

const RequestMoreInfoWrapper = styled.div`
  margin-top: 35px;
  & > div {
    align-items: flex-start;
    & > div:last-child {
      padding: 15px 0;

      & div:first-child {
        font-weight: 600;
      }
    }
  }

  .request-more-info-content {
    margin: 10px 0;
  }
`;

const CertificateTableWrapper = styled.div`
  margin-top: 40px;
  table tr {
    position: relative;
    &:has(.expired) {
      background-color: #d9d9d940;
    }
  }
  & div.expired {
    height: 100%;
    &::before {
      content: '';
      position: absolute;
      width: 10px;
      height: 100%;
      left: 0;
      top: 0;
      background-color: ${({ theme }) => theme.colors.alert.errorDark};
    }
  }
`;

const ExpiryText = styled.span`
  display: inline-block;
  width: 90px;
`;

const EndorsementTableWrapper = styled.div`
  margin-top: 40px;
`;

const DispensationsTableWrapper = styled.div`
  margin-top: 40px;
  table tr {
    position: relative;
    &:has(.expired) {
      background-color: #d9d9d940;
    }
  }
  & div.expired {
    height: 100%;
    &::before {
      content: '';
      position: absolute;
      width: 10px;
      height: 100%;
      left: 0;
      top: 0;
      background-color: ${({ theme }) => theme.colors.alert.errorDark};
    }
  }
`;

const CustomButton = styled(SdirButton)`
  height: auto;
  padding: 15px 20px 15px 50px;
  & > span > div {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    span:first-child {
      font-weight: 600;
      margin-bottom: 5px;
    }

    svg {
      position: absolute;
      top: 3px;
      left: -30px;
    }
  }
`;

const DaysLeft = styled.span`
  display: inline-block;
  margin-left: 10px;
  padding: 6px;
  border-radius: 5px;
  border 1px solid #c0b86f;
  background-color: #faf8f0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;

  .empty-title {
    font-size: 24px;
    font-weight: 600;
    margin: 10px 0 20px 0;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    img {
      width: 240px;
      height: 226px;
    }
    .empty-title {
      font-size: 22px;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  & > button:not(:last-child) {
    margin-right: 16px;
  }

  &.centered {
    justify-content: center;
  }
`;

const StyledModal = styled(SdirModal)`
  max-height: 95vh;
  overflow-y: scroll;
  font-size: 16px;
  p {
    margin: 0;
  }
`;

const StyledDataTable = styled(DataTable)`
  margin-left: -5px;
  background-color: transparent;
  thead tr th {
    button {
      text-align: left;
    }
  }
  tbody > tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    & > td {
      padding-left: 14px;
      &:last-child {
        padding-right: 14px !important;
      }
    }

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }
  }
`;
